// Feature: docs/features/edf-generation.md
import React, { useState, useContext, useRef } from 'react';
import {
  Box,
  VStack,
  HStack,
  Button,
  Textarea,
  Text,
  useToast,
  Heading,
  Divider,
  Alert,
  AlertIcon,
  Badge
} from '@chakra-ui/react';
import { AttachmentIcon } from '@chakra-ui/icons';
import { AppContext } from '../AppContext';
import { callApi } from '../../callApi';

const EDFUploadTab = ({ onJobCreated }) => {
  const [manualIds, setManualIds] = useState('');
  const [parsedIds, setParsedIds] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const { accessToken } = useContext(AppContext);
  const toast = useToast();
  const fileInputRef = useRef(null);

  const extractIds = (text) => {
    // Match 6-digit numbers
    const regex = /\b\d{6}\b/g;
    const matches = text.match(regex) || [];
    // Remove duplicates
    return [...new Set(matches)];
  };

  const handleManualChange = (e) => {
    const text = e.target.value;
    setManualIds(text);
    setParsedIds(extractIds(text));
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target.result;
      const extracted = extractIds(text);
      
      if (extracted.length > 0) {
        setManualIds(text);
        setParsedIds(extracted);
        toast({
          title: 'File parsed',
          description: `Found ${extracted.length} valid 6-digit Case IDs.`,
          status: 'success',
          duration: 3000,
        });
      } else {
        toast({
          title: 'No IDs found',
          description: 'Could not find any 6-digit Case IDs in the uploaded file.',
          status: 'warning',
          duration: 5000,
        });
      }
    };
    reader.readAsText(file);
    // Reset input so the same file can be uploaded again if needed
    e.target.value = null;
  };

  const handleSubmit = async () => {
    if (parsedIds.length === 0) {
      toast({
        title: 'No IDs to submit',
        status: 'warning',
        duration: 3000,
      });
      return;
    }

    setIsSubmitting(true);
    try {
      const response = await callApi('/api/v1/edf-jobs', accessToken, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ case_ids: parsedIds })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to create job');
      }

      toast({
        title: 'Job Created',
        description: `Successfully queued ${parsedIds.length} Case IDs for EDF generation.`,
        status: 'success',
        duration: 5000,
      });

      // Reset form
      setManualIds('');
      setParsedIds([]);
      
      // Notify parent to refresh jobs list
      if (onJobCreated) {
        onJobCreated();
      }

    } catch (error) {
      toast({
        title: 'Error',
        description: error.message,
        status: 'error',
        duration: 5000,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <VStack align="stretch" spacing={6}>
      <Box>
        <Heading size="sm" mb={2}>Upload CSV or Enter IDs</Heading>
        <Text fontSize="sm" color="gray.600" mb={4}>
          Provide a list of 6-digit Case IDs. You can upload a CSV file or paste them directly into the text area below.
        </Text>

        <HStack mb={4}>
          <Button
            leftIcon={<AttachmentIcon />}
            onClick={() => fileInputRef.current?.click()}
            colorScheme="gray"
            variant="outline"
          >
            Upload CSV
          </Button>
          <input
            type="file"
            accept=".csv,.txt"
            ref={fileInputRef}
            style={{ display: 'none' }}
            onChange={handleFileUpload}
          />
        </HStack>

        <Textarea
          value={manualIds}
          onChange={handleManualChange}
          placeholder="Paste Case IDs here (e.g., 123456, 654321)..."
          minH="200px"
          mb={4}
        />

        {parsedIds.length > 0 ? (
          <Alert status="info" rounded="md" mb={4}>
            <AlertIcon />
            Found {parsedIds.length} valid Case ID(s) ready for processing.
          </Alert>
        ) : (
          manualIds.length > 0 && (
            <Alert status="warning" rounded="md" mb={4}>
              <AlertIcon />
              No valid 6-digit Case IDs detected in the input.
            </Alert>
          )
        )}

        <Button
          colorScheme="blue"
          onClick={handleSubmit}
          isLoading={isSubmitting}
          isDisabled={parsedIds.length === 0}
        >
          Generate EDF
        </Button>
      </Box>
    </VStack>
  );
};

export default EDFUploadTab;
